import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';
import { MILESTONE_BONUSES } from './config/exploreUnknownLevelingMilestones';
import './ExploreUnknownMilestoneUnlockOverlay.css';

/**
 * Full-screen milestone unlock reveal on AfterBattle (Explore Unknown win).
 *
 * @param {{
 *   milestoneLevel?: number,
 *   variant?: 'milestone' | 'doorUnlock',
 *   onComplete: () => void,
 * }} props
 */
export default function ExploreUnknownMilestoneUnlockOverlay({
  milestoneLevel,
  variant = 'milestone',
  onComplete,
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('enter');
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const isDoorUnlock = variant === 'doorUnlock';
  const level = Number(milestoneLevel);
  const meta = isDoorUnlock ? null : MILESTONE_BONUSES[String(level)];
  if (!isDoorUnlock && !meta) {
    throw new Error(`ExploreUnknownMilestoneUnlockOverlay: unknown milestone level ${milestoneLevel}`);
  }

  const title = isDoorUnlock
    ? t('afterBattle.exploreUnknownDoorUnlock.title')
    : t(`exploreUnknownPage.${meta.i18nKey}.title`);
  const message = isDoorUnlock
    ? t('exploreUnknownPage.level100DoorUnlockedMessage')
    : t(`afterBattle.exploreUnknownMilestoneUnlock.level${level}`);
  const eyebrow = isDoorUnlock
    ? t('afterBattle.exploreUnknownDoorUnlock.eyebrow')
    : t('afterBattle.exploreUnknownMilestoneUnlock.eyebrow');
  const iconSrc = isDoorUnlock ? '/images/explore_end_100.jpg' : meta.icon;
  const badgeLevel = isDoorUnlock ? 100 : level;

  const finish = useCallback(() => {
    setPhase('exit');
    window.setTimeout(() => onCompleteRef.current(), 380);
  }, []);

  useEffect(() => {
    setPhase('enter');
    playSoundEffect('/sounds/legendary_reveal.mp3');
    const revealTimer = window.setTimeout(() => setPhase('shown'), 80);
    return () => clearTimeout(revealTimer);
  }, [level, variant]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      finish();
    }
  };

  const root = (
    <div
      className={`eu-milestone-unlock${phase === 'exit' ? ' eu-milestone-unlock--exit' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="eu-milestone-unlock-title"
      aria-describedby="eu-milestone-unlock-message"
      onClick={handleBackdropClick}
    >
      <div className="eu-milestone-unlock-backdrop" aria-hidden="true" />
      <div className="eu-milestone-unlock-rays" aria-hidden="true" />
      <div className="eu-milestone-unlock-card">
        <span className="eu-milestone-unlock-spark eu-milestone-unlock-spark--a" aria-hidden="true" />
        <span className="eu-milestone-unlock-spark eu-milestone-unlock-spark--b" aria-hidden="true" />
        <span className="eu-milestone-unlock-spark eu-milestone-unlock-spark--c" aria-hidden="true" />

        <div className="eu-milestone-unlock-badge" aria-hidden="true">
          <span className="eu-milestone-unlock-badge-label">LV</span>
          <span className="eu-milestone-unlock-badge-value">{badgeLevel}</span>
        </div>

        <div className="eu-milestone-unlock-icon-wrap">
          <span className="eu-milestone-unlock-icon-ring" aria-hidden="true" />
          <img
            src={iconSrc}
            alt=""
            className="eu-milestone-unlock-icon"
            draggable={false}
          />
        </div>

        <p className="eu-milestone-unlock-eyebrow">{eyebrow}</p>
        <h2 id="eu-milestone-unlock-title" className="eu-milestone-unlock-title">
          {title}
        </h2>
        <p id="eu-milestone-unlock-message" className="eu-milestone-unlock-message">
          {message}
        </p>

        <button
          type="button"
          className="eu-milestone-unlock-continue"
          onClick={(e) => {
            e.stopPropagation();
            finish();
          }}
        >
          {t('afterBattle.exploreUnknownMilestoneUnlock.continue')}
        </button>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return root;
  }
  return createPortal(root, document.body);
}
